// Billing › Invoice Workbook: the per-job invoice terms and exception tables the
// "Build invoice folder" button prices and checks with (phase 2, Seth 11/2/26).
// They were hand-coded into each period's invoice script before: the PO line and
// the mobilization credit, card classifications the rate sheet spells differently
// or does not carry, people the Attendance & Force Report master does not map, and
// card units that bill as another rate sheet item or stay off the invoice.
//
// Stored as Billing user-data rows (one JSON blob per row, like the rate sheet's
// 'rate' / 'rule' rows), never in the job's single 1024-character settings blob:
//   fieldName 'invoice'  rowId 'invoice_terms'        Notes { po, poLine, poLineName, afe, terms, so, mobBalance, mobBasis }
//                        rowId 'invoice_cls_<n>'      Notes { card, as }      as '' = keep the card's wording
//                        rowId 'invoice_person_<id>'  Notes { id, name, note } accepted off the master, by Emp ID
//                        rowId 'invoice_unit_<n>'     Notes { match, as, skip } match = card description or unit no
// A row with blank Notes is a deleted one (the same convention as the rate rows).

import moment from 'moment';

export const INVOICE_FIELD = 'invoice';
export const TERMS_ROW = 'invoice_terms';
/** The VoltaGrid jobs: the other jobs a person's straight time counts across. */
export const VOLTAGRID_JOBS = ['54026070', '54026071', '54026076', '54026077'];
/** Payroll hour types that count toward the 40 straight-time hours: regular, overtime, salaried over 40. */
export const HOUR_PAY_TYPES = [1, 5, 7];

const parseNotes = (n) => { try { return n ? JSON.parse(n) : null; } catch (e) { return null; } };
const str = (v) => String(v ?? '').trim();
const num = (v) => { const n = parseFloat(String(v ?? '').replace(/[$,\s]/g, '')); return Number.isFinite(n) ? n : 0; };

/**
 * Card and master wording the rate sheet spells differently (the invoice scripts'
 * CLASS_ALIAS with Seth's 10/30 calls). Keyed by the normalized text.
 */
export const CLASS_ALIAS = {
    Director: 'Project Director',
    'Senior Director': 'Project Director',
    'HR Business Partner I': 'Office Manager',
    'Construction Manager/APM': 'Construction Manager',
    'Crane operator': 'Crane Operator',
    'Common Labor': 'Common Laborer',
};
/** One spelling for a classification: case, runs of spaces and the spaces around "/" and "-" do not matter ("Electrician Apprentice 3rd/4th year"). */
export const normCls = (s) => String(s || '').replace(/&amp;/g, '&').toLowerCase().replace(/\s*([/-])\s*/g, '$1').replace(/\s+/g, ' ').trim();
const ALIAS_BY_NORM = Object.fromEntries(Object.entries(CLASS_ALIAS).map(([k, v]) => [normCls(k), v]));
/**
 * The rate sheet line a classification bills under: the sheet's own spelling when the
 * text (or its alias) is a line, else ''. laborRows are the job's rate sheet labor rows.
 */
export const sheetClassOf = (cls, laborRows) => {
    const byNorm = {};
    (laborRows || []).forEach((r) => { if (r && r.name) byNorm[normCls(r.name)] = r.name; });
    const n = normCls(cls);
    if (!n) return '';
    return byNorm[n] || byNorm[normCls(ALIAS_BY_NORM[n] || '')] || '';
};

export const EMPTY_TERMS = { po: '', poLine: '', poLineName: '', afe: '', terms: 'Net 30', so: '', mobBalance: '', mobBasis: 'remaining mobilization invoice credit balance' };

/**
 * The job's invoice rows as one object: { terms, classes, people, units }, each
 * table row carrying its rowId so an edit or a delete goes back to the same row.
 */
export const parseInvoiceExceptions = (userRows) => {
    const out = { terms: { ...EMPTY_TERMS }, classes: [], people: [], units: [] };
    (userRows || []).forEach((x) => {
        if (x?.Forecast_Field_Name !== INVOICE_FIELD) return;
        const notes = str(x.Notes);
        if (!notes) return; // deleted
        const p = parseNotes(notes);
        if (!p) return;
        const rowId = String(x.Forecast_Row_ID || '');
        if (rowId === TERMS_ROW) {
            out.terms = { ...EMPTY_TERMS, ...Object.fromEntries(Object.entries(p).map(([k, v]) => [k, k === 'mobBalance' ? (v === '' || v == null ? '' : num(v)) : str(v)])) };
        } else if (rowId.startsWith('invoice_cls_')) {
            if (str(p.card)) out.classes.push({ rowId, card: str(p.card), as: str(p.as) });
        } else if (rowId.startsWith('invoice_person_')) {
            if (str(p.id)) out.people.push({ rowId, id: str(p.id).replace(/^0+(?=\d)/, ''), name: str(p.name), note: str(p.note) });
        } else if (rowId.startsWith('invoice_unit_')) {
            if (str(p.match)) out.units.push({ rowId, match: str(p.match), as: str(p.as), skip: Boolean(p.skip) || !str(p.as) });
        }
    });
    out.classes.sort((a, b) => a.card.localeCompare(b.card));
    out.people.sort((a, b) => a.name.localeCompare(b.name));
    out.units.sort((a, b) => a.match.localeCompare(b.match));
    return out;
};

/** The invoice terms in the shape the folder's documents take: { po, poLine, poLineName, afe, terms, so, mobCredit }. */
export const invoiceTermsFor = (x) => {
    const t = { ...EMPTY_TERMS, ...((x && x.terms) || {}) };
    const balance = num(t.mobBalance);
    return {
        po: t.po, poLine: t.poLine, poLineName: t.poLineName, afe: t.afe, terms: t.terms || 'Net 30', so: t.so,
        mobCredit: balance > 0 ? { balance, basis: t.mobBasis || EMPTY_TERMS.mobBasis } : null,
    };
};

// ── rows to save (billing.js saveRows: { fieldName, rowId, notes }) ──
const newId = (prefix) => `${prefix}${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
export const termsRow = (terms) => ({
    fieldName: INVOICE_FIELD, rowId: TERMS_ROW,
    notes: JSON.stringify(Object.fromEntries(Object.entries({ ...EMPTY_TERMS, ...terms }).map(([k, v]) => [k, k === 'mobBalance' ? (str(v) === '' ? '' : num(v)) : str(v)]))),
});
export const classRow = (r) => ({ fieldName: INVOICE_FIELD, rowId: r.rowId || newId('invoice_cls_'), notes: JSON.stringify({ card: str(r.card), as: str(r.as) }) });
export const personRow = (r) => ({ fieldName: INVOICE_FIELD, rowId: `invoice_person_${str(r.id).replace(/^0+(?=\d)/, '')}`, notes: JSON.stringify({ id: str(r.id).replace(/^0+(?=\d)/, ''), name: str(r.name), note: str(r.note) }) });
export const unitRow = (r) => ({ fieldName: INVOICE_FIELD, rowId: r.rowId || newId('invoice_unit_'), notes: JSON.stringify({ match: str(r.match), as: r.skip ? '' : str(r.as), skip: Boolean(r.skip) || !str(r.as) }) });
export const deleteRow = (rowId) => ({ fieldName: INVOICE_FIELD, rowId, notes: '' });

// ── JDE payroll hours on the other jobs ──
/** A JDE Julian date (1yyddd) as YYYY-MM-DD. */
export const jdeDate = (dwk) => {
    const n = Number(dwk);
    if (!(n > 100000)) return '';
    const yy = Math.floor(n / 1000) - 100;
    const ddd = n % 1000;
    return moment(`${2000 + yy}-01-01`, 'YYYY-MM-DD').add(ddd - 1, 'days').format('YYYY-MM-DD');
};
/**
 * readJdePayRates rows ({ id, job, dwk, pt, hrs }) as the model's otherHours:
 * { [we]: { [id]: { [job]: { [date]: hrs } } } }, the jobs other than `job`, hour
 * pay types only (1 regular, 5 overtime, 7 salaried over 40).
 */
export const otherHoursFromPay = (rows, job, weeks) => {
    const out = {};
    const weekSet = new Set(weeks || []);
    const weekEnd = (d) => { const m = moment(d, 'YYYY-MM-DD'); return m.add((7 - m.day()) % 7, 'days').format('YYYY-MM-DD'); };
    (rows || []).forEach((r) => {
        if (String(r.job) === String(job) || !HOUR_PAY_TYPES.includes(Number(r.pt))) return;
        const h = Number(r.hrs) || 0;
        if (!(h > 0)) return;
        const d = jdeDate(r.dwk);
        if (!d) return;
        const we = weekEnd(d);
        if (weekSet.size && !weekSet.has(we)) return;
        const id = String(Number(r.id));
        const byJob = (out[we] || (out[we] = {}))[id] || (out[we][id] = {});
        const days = byJob[String(r.job)] || (byJob[String(r.job)] = {});
        days[d] = Math.round(((days[d] || 0) + h) * 100) / 100;
    });
    return out;
};
